// src/services/azureBlobService.js
// Azure Blob Storage Service für Bildverwaltung
import {
  saveImage,
  getImage,
  deleteImage as deleteImageMeta,
} from "./azureProjectImagesService";

const AZURE_BLOB_CONFIG = {
  storageAccount: "mfprojects",
  containerName: "project-images",
  sasToken:
    "sv=2024-11-04&ss=b&srt=co&sp=rwdlaciytfx&se=2035-10-02T19:07:10Z&st=2025-10-02T10:52:10Z&spr=https&sig=A8jWH2s8LkqmqiQjwjR8bL4sLjDKgKlE1HkDCZSlsYk%3D",
};

// Validierung
const validateConfig = () => {
  if (!AZURE_BLOB_CONFIG.storageAccount) {
    throw new Error("Azure Storage Account nicht konfiguriert");
  }
  if (!AZURE_BLOB_CONFIG.containerName) {
    throw new Error("Blob Container Name nicht konfiguriert");
  }
  if (
    !AZURE_BLOB_CONFIG.sasToken ||
    AZURE_BLOB_CONFIG.sasToken === "YOUR_SAS_TOKEN_HERE"
  ) {
    throw new Error(
      "SAS Token für Blob Storage nicht konfiguriert. Bitte in azureBlobService.js eintragen."
    );
  }
};

const validateFile = (file, kategorie) => {
  const maxSize =
    kategorie === "main-picture" ? 20 * 1024 * 1024 : 10 * 1024 * 1024;
  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "video/mp4",
    "video/quicktime",
    "video/webm",
    "image/gif",
  ];

  if (!file) throw new Error("Keine Datei ausgewählt");
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Ungültiges Format: ${file.type}`);
  }
  if (file.size > maxSize) {
    throw new Error(
      `Datei zu gross: ${(file.size / 1024 / 1024).toFixed(1)}MB. Max: ${
        kategorie === "main-picture" ? "20" : "10"
      }MB`
    );
  }
  return true;
};

// Bild in verschiedenen Auflösungen hochladen
export const uploadImage = async (
  projectId,
  kategorie,
  file,
  bildtext,
  isPublic
) => {
  try {
    validateConfig();
    validateFile(file, kategorie);

    if (!projectId) throw new Error("Projekt-ID fehlt");
    if (!kategorie) throw new Error("Kategorie fehlt");

    const bildId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileExt = file.name.split(".").pop().toLowerCase();
    const isVideo = file.type.startsWith("video/");

    const urls = {};

    if (isVideo) {
      const blobPath = `${projectId}/${kategorie}/${bildId}/original.${fileExt}`;
      urls.original = await uploadBlob(blobPath, file, file.type);
    } else {
      const sizes = [
        { name: "original", width: 2300 },
        { name: "xl", width: 1600 },
        { name: "large", width: 1000 },
        { name: "medium", width: 400 },
        { name: "thumbnail", width: 200 },
      ];

      for (const size of sizes) {
        const resizedBlob = await resizeImage(file, size.width);
        const blobPath = `${projectId}/${kategorie}/${bildId}/${size.name}.${fileExt}`;
        urls[size.name] = await uploadBlob(blobPath, resizedBlob, file.type);
      }
    }

    const imageMetadata = {
      bildId,
      projectId,
      kategorie,
      bildtext: bildtext || "",
      isPublic: isPublic || false,
      type: isVideo ? "video" : "image",
      urls,
      originalFileName: file.name,
      fileSize: file.size,
      createdAt: new Date().toISOString(),
    };

    await saveImageMetadata(imageMetadata);
    return imageMetadata;
  } catch (error) {
    throw new Error(`Upload fehlgeschlagen: ${error.message}`);
  }
};

// Blob hochladen
const uploadBlob = async (blobPath, blob, contentType) => {
  try {
    const url = `https://${AZURE_BLOB_CONFIG.storageAccount}.blob.core.windows.net/${AZURE_BLOB_CONFIG.containerName}/${blobPath}?${AZURE_BLOB_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": contentType || blob.type,
      },
      body: blob,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Blob Upload Error Response:", errorText);

      if (response.status === 404) {
        throw new Error(
          `Container '${AZURE_BLOB_CONFIG.containerName}' nicht gefunden. Bitte im Azure Portal erstellen.`
        );
      } else if (response.status === 403) {
        throw new Error(
          "Zugriff verweigert. SAS Token ist ungültig oder abgelaufen."
        );
      } else if (response.status === 400) {
        throw new Error(
          "Ungültige Anfrage. Prüfe SAS Token und Container-Name."
        );
      }

      throw new Error(
        `Blob Upload fehlgeschlagen (${response.status}): ${errorText}`
      );
    }

    return url.split("?")[0];
  } catch (error) {
    if (error.message.includes("fetch")) {
      throw new Error("Netzwerkfehler. Prüfe deine Internetverbindung.");
    }
    throw error;
  }
};

// Bild verkleinern
// Bild verkleinern - PROPORTIONEN BEIBEHALTEN
const resizeImage = (file, maxWidth) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      try {
        // Originalgrösse beibehalten wenn kleiner als maxWidth
        let width = img.width;
        let height = img.height;

        // Nur skalieren wenn nötig
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio; // Proportional skalieren
        }

        canvas.width = width;
        canvas.height = height;

        // Bild mit korrekten Proportionen zeichnen
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Canvas zu Blob Konvertierung fehlgeschlagen"));
            }
          },
          file.type,
          0.95
        ); // Qualität auf 0.95 erhöht für bessere Bildqualität
      } catch (error) {
        reject(new Error(`Bildverarbeitung fehlgeschlagen: ${error.message}`));
      }
    };

    img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));

    try {
      img.src = URL.createObjectURL(file);
    } catch (error) {
      reject(new Error("Fehler beim Lesen der Datei"));
    }
  });
};

// Bild löschen
export const deleteImage = async (projectId, kategorie, bildId) => {
  try {
    console.log("Delete Start:", { projectId, kategorie, bildId });

    validateConfig();

    const metadata = await getImageMetadata(projectId, bildId);

    if (!metadata) {
      console.warn("Keine Metadaten gefunden, lösche nur Metadaten-Eintrag");
      await deleteImageMetadata(projectId, bildId);
      return;
    }

    const sizes = ["original", "xl", "large", "medium", "thumbnail"];
    const fileExt = metadata.originalFileName.split(".").pop().toLowerCase();

    for (const size of sizes) {
      try {
        const blobPath = `${projectId}/${kategorie}/${bildId}/${size}.${fileExt}`;
        await deleteBlob(blobPath);
        console.log(`${size} gelöscht`);
      } catch (error) {
        console.warn(`Fehler beim Löschen von ${size}:`, error.message);
      }
    }

    await deleteImageMetadata(projectId, bildId);
    console.log("Bild erfolgreich gelöscht");
  } catch (error) {
    console.error("Delete Error:", error);
    throw new Error(`Löschen fehlgeschlagen: ${error.message}`);
  }
};

// Blob löschen
const deleteBlob = async (blobPath) => {
  const url = `https://${AZURE_BLOB_CONFIG.storageAccount}.blob.core.windows.net/${AZURE_BLOB_CONFIG.containerName}/${blobPath}?${AZURE_BLOB_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "DELETE",
  });

  if (!response.ok && response.status !== 404) {
    const errorText = await response.text();
    throw new Error(
      `Blob Delete fehlgeschlagen (${response.status}): ${errorText}`
    );
  }
};

// Metadaten-Funktionen
const saveImageMetadata = async (metadata) => {
  return saveImage(metadata);
};

const getImageMetadata = async (projectId, bildId) => {
  return getImage(projectId, bildId);
};

const deleteImageMetadata = async (projectId, bildId) => {
  return deleteImageMeta(projectId, bildId);
};
